import {
  Alert,
  AlertIcon,
  HStack,
  useToast,
  VStack,
  Text,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { boleto } from 'boleto-brasileiro-validator';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import InputNumber from 'components/InputNumber';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import useAuth from 'contexts/useAuth';
import { queryClient } from 'index';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createBilletPayment, validateBillet } from 'services/api.service';
import { formatCurrency } from 'utils/number';
import { billetPaymentWindowBlock, BilletPaymentWindow } from './billetPaymentWindow';

interface BilletResponse {
  id: string;
  integrationId: string;
  billetCode: string;
  uniqueCode: string;
  ipsbCode: string;
  ispbName: string;
  beneficiaryName: string;
  beneficiaryDocument: string;
  finalBeneficiaryName: string;
  finalBeneficiaryDocument: string;
  amount: string;
  dueDate: string;
  limitDueDate: string;
  allowChangeValue: boolean;
  discount: string;
  fee: string;
  finalAmount: string;
  minValue: string;
  maxValue: string;
  createdAt: string;
  updatedAt: string;
  paymentWindow?: BilletPaymentWindow;
}

interface CreatePaymentParams {
  barcode: string;
  description: string;
  amount?: string;
}

const BarcodePayment: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const toast = useToast();
  const { updateUserData } = useAuth();
  const { requestTwoFa } = useTwoFaHelper();
  const [billetData, setBilletData] = React.useState<BilletResponse | null>(null);

  // Fora da janela bancária o boleto pode ser consultado, mas não pago.
  const windowBlock = billetPaymentWindowBlock(billetData?.paymentWindow);

  const { control, handleSubmit, setValue, setError } = useForm<CreatePaymentParams>();
  const { mutateAsync: findBillet, isLoading: isLoadingFindBillet } = useMutation({
    mutationFn: validateBillet,
  });

  const { mutateAsync: createPayment, isLoading: isLoadingCreatePayment } = useMutation({
    mutationFn: createBilletPayment,
  });

  const handleCreatePayment = async (data: CreatePaymentParams) => {
    if (windowBlock.blocked) {
      toast({
        title: 'Fora do horário',
        description: windowBlock.message,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    const twoFaId = await requestTwoFa();

    if (!twoFaId?.id) {
      return;
    }

    try {
      await createPayment({ billetId: billetData?.id, amount: Number(data.amount), twoFaId: twoFaId.id });
      updateUserData();
      onClose();
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });

      toast({
        title: 'Sucesso',
        description: 'Pagamento criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        // O servidor recusa o pagamento fora da janela — mostra o aviso dele.
        description: error?.response?.data?.message ?? 'Erro ao criar pagamento',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleFindBillet = async (data: CreatePaymentParams) => {
    const isValid = boleto(data.barcode);
    if (!isValid) {
      setError('barcode', {
        message: 'Código de barras inválido',
      });
      toast({
        title: 'Erro',
        description: 'Código de barras inválido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const billet = await findBillet(data.barcode);
      setBilletData(billet);
      const payableAmount = Number(billet.finalAmount) > 0 ? billet.finalAmount : billet.amount;
      setValue('amount', payableAmount);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao buscar boleto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Criar pagamento de boleto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack
              spacing={5}
              alignItems="start"
            >
              <HStack
                width="100%"
                justifyContent="space-between"
                alignItems="flex-end"
              >
                <InputForm
                  control={control}
                  label="Código de Barras"
                  name="barcode"
                  type="text"
                  isDisabled={!!billetData}
                  onChange={(value) => {
                    setValue('barcode', value);
                  }}
                  rules={{
                    required: 'Campo obrigatório',
                    minLength: {
                      value: 40,
                      message: 'Código de barras inválido',
                    },
                  }}
                />
              </HStack>

              <CustomButton
                w="100%"
                colorScheme="green"
                isDisabled={!!billetData}
                isLoading={isLoadingFindBillet}
                onClick={handleSubmit(handleFindBillet)}
              >
                Buscar Boleto
              </CustomButton>

              {billetData && (
                <Box width="100%">
                  <VStack
                    spacing={3}
                    align="stretch"
                  >
                    {windowBlock.blocked && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">{windowBlock.message}</Text>
                      </Alert>
                    )}
                    <Text>
                      <strong>Beneficiário:</strong> {billetData.beneficiaryName}
                    </Text>
                    <Text>
                      <strong>Documento do Beneficiário:</strong> {billetData.beneficiaryDocument}
                    </Text>
                    <Text>
                      <strong>Banco:</strong> {billetData.ispbName} ({billetData.ipsbCode})
                    </Text>
                    <Text>
                      <strong>Data de Vencimento:</strong> {new Date(billetData.dueDate).toLocaleDateString()}
                    </Text>
                    <Text>
                      <strong>Data Limite:</strong> {new Date(billetData.limitDueDate).toLocaleDateString()}
                    </Text>
                    <Text>
                      <strong>Valor Original:</strong> {formatCurrency(+billetData.amount)}
                    </Text>
                    {Number(billetData.discount) > 0 && (
                      <Text>
                        <strong>Desconto:</strong> {formatCurrency(+billetData.discount)}
                      </Text>
                    )}
                    {Number(billetData.fee) > 0 && (
                      <Text>
                        <strong>Taxa:</strong> {formatCurrency(+billetData.fee)}
                      </Text>
                    )}

                    {billetData.allowChangeValue && Number(billetData.finalAmount) === 0 ? (
                      <>
                        {billetData.minValue && billetData.maxValue && (
                          <Text
                            fontSize="sm"
                            color="gray.600"
                          >
                            Valor permitido entre {formatCurrency(+billetData.minValue)} e{' '}
                            {formatCurrency(+billetData.maxValue)}
                          </Text>
                        )}
                        <InputNumber
                          control={control}
                          label="Valor a Pagar"
                          name="amount"
                          rules={{
                            required: 'Campo obrigatório',
                            validate: (value: number) => {
                              const original = Number(billetData.amount);
                              if (Math.abs(value - original) < 0.01) return true;
                              const min = Number(billetData.minValue);
                              const max = Number(billetData.maxValue);
                              if (value < min) return `Valor mínimo permitido é ${formatCurrency(min)}`;
                              if (value > max) return `Valor máximo permitido é ${formatCurrency(max)}`;
                              return true;
                            },
                          }}
                        />
                      </>
                    ) : (
                      <Text>
                        <strong>Valor a Pagar:</strong>{' '}
                        {formatCurrency(
                          Number(billetData.finalAmount) > 0
                            ? +billetData.finalAmount
                            : +billetData.amount,
                        )}
                      </Text>
                    )}
                    <CustomButton
                      w="100%"
                      colorScheme="green"
                      isDisabled={windowBlock.blocked}
                      isLoading={isLoadingCreatePayment}
                      onClick={handleSubmit(handleCreatePayment)}
                    >
                      Criar Pagamento
                    </CustomButton>
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BarcodePayment;
